import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiFetch } from '@/lib/api/client';
import { useSession } from '@/lib/auth/session';

/**
 * 성과급 (V308 · 사용자 지시 2026-09-21) — **현장이거나 부서**다.
 *
 * ## 타입을 한 곳에 둔다
 * 이 자료를 읽는 화면이 둘이다 — 인사자료 관리 › 성과급(본사)과 마이페이지 › 복지관리 › 성과급
 * (평가자·본인). 보는 각도는 다르지만 한 건을 펼쳤을 때 읽는 값은 같아서, 각자 타입을 두면
 * 칸이 하나 붙을 때마다 두 곳을 고쳐야 하고 언젠가 한쪽만 고쳐진다(`promotionDraft` 와 같은 판단).
 *
 * ## 금액은 서버가 낸다
 * 화면이 보내는 것은 **기여도(%)** 하나다. `기준 금액 × 기여도 ÷ 100` 은 서버의 몫이라, 저장하고
 * 돌아온 응답이 곧 금액이다 — 화면에서 미리 곱해 보여 주면 반올림이 갈린다.
 *
 * ## 마이페이지는 잠겨 있다
 * 사용자 지시 2026-09-21: 「인사고과나 급여명세서처럼 비밀번호 한번 더 입력해야 나오게」.
 * 그래서 `useMyIncentive()` 는 **건수만** 받고, 금액은 `useUnlockMyIncentive()` 를 지나야 온다 —
 * 화면에서 감추기만 하면 응답에 이미 값이 있어 개발자 도구로 보인다.
 */

/** 건이 붙는 조직. 배치표의 `assignment_type` 과 같은 값이다. */
export type IncentiveOwnerType = 'SITE' | 'DEPARTMENT';

export const OWNER_TYPE_LABELS: Record<IncentiveOwnerType, string> = {
  SITE: '현장',
  DEPARTMENT: '부서',
};

/** 그 조직에서 평가를 넣는 자리. 권장 100% 가 이 사람 줄에 걸린다. */
export const OWNER_LEAD_LABELS: Record<IncentiveOwnerType, string> = {
  SITE: '현장대리인',
  DEPARTMENT: '팀장',
};

/** 장의 줄에 붙는 이름표 — 현장은 그 줄의 담당(현장소장 · 현장대리인), 부서는 팀장 (현장소장 복원 2026-10-28). */
export function ownerLeadLabel(ownerType: IncentiveOwnerType, dutyTitle: string | null | undefined): string {
  return ownerType === 'SITE' && dutyTitle === '현장소장' ? dutyTitle : OWNER_LEAD_LABELS[ownerType];
}

export type IncentiveStatus = 'DRAFT' | 'SUBMITTED' | 'ADJUSTING' | 'APPROVING' | 'CONFIRMED';

/** 사람이 읽는 상태 이름. 서버(`SiteIncentiveService.label`)와 같은 말을 써야 한다. */
export const INCENTIVE_STATUS_LABELS: Record<IncentiveStatus, string> = {
  DRAFT: '현장 평가 중',
  SUBMITTED: '본사 제출',
  ADJUSTING: '협의·조정',
  APPROVING: '결재 중',
  CONFIRMED: '확정',
};

/**
 * 목록에 적는 상태 — **「현장 평가 중」이 거짓말이 되는 한 경우**를 가른다.
 *
 * 준공이 다가오면 서버가 건을 연다(V308 · 자동 개설). 그 건은 한도·기준 인원이 비어 있고,
 * 그동안은 현장대리인 화면에 **뜨지도 않는다** — 그런데 상태만 보면 「현장 평가 중」이라 인사팀은
 * 현장이 뭉개고 있다고 읽는다. 실제로 기다리는 쪽은 본사다.
 *
 * 설명을 화면에 더 붙이는 대신 **상태 이름을 정확히** 적는다.
 */
export function statusLabel(
  row: { status: IncentiveStatus; budgetAmount: number | null; baseHeadcount: number | null },
): string {
  if (row.status === 'DRAFT' && (row.budgetAmount === null || !row.baseHeadcount)) return '한도 미입력';
  return INCENTIVE_STATUS_LABELS[row.status];
}

export type IncentiveSummary = {
  id: string;
  ownerType: IncentiveOwnerType;
  siteId: string | null;
  departmentId: string | null;
  ownerName: string;
  constructionStartDate: string | null;
  constructionEndDate: string | null;
  grade: string | null;
  baseHeadcount: number | null;
  budgetAmount: number | null;
  /** 한도 ÷ 기준 인원. 서버가 낸다 — 둘 중 하나가 비면 null 이다(0 이 아니다). */
  baseAmount: number | null;
  status: IncentiveStatus;
  memberCount: number;
  totalAmount: number;
  /** 한도 − 합계. **음수로 내려간다** — 얼마나 넘었는지를 알아야 어느 줄을 깎을지 정한다. */
  remainingAmount: number | null;
  approvalDocumentId: string | null;
  submittedAt: string | null;
  confirmedAt: string | null;
  /** 이 건을 조정할 수 있는가 — 서버가 건마다 판정한다(DOC-118 §18). 옛 서버는 안 보낸다. */
  canEdit?: boolean;
};

export type IncentiveAward = {
  personnelId: string;
  personnelName: string;
  rankName: string | null;
  dutyTitle: string | null;
  /** 그 조직의 장 본인 줄(현장대리인·팀장). 권장 100% 가 채워져 온다 — 못 박은 값이 아니라 채워 준 값이다. */
  ownerLead: boolean;
  joinedOn: string | null;
  leftOn: string | null;
  months: number | null;
  contributionRate: number | null;
  /** 현장안(기준 금액 × 기여도). */
  amount: number | null;
  /** 본사 조정액. **비어 있으면 조정하지 않았다**는 뜻이고, 0 과 다르다(0 은 「안 준다」). */
  adjustedAmount: number | null;
  payableAmount: number | null;
  note: string | null;
};

export type IncentiveDetail = {
  id: string;
  ownerType: IncentiveOwnerType;
  siteId: string | null;
  departmentId: string | null;
  ownerName: string;
  constructionStartDate: string | null;
  constructionEndDate: string | null;
  grade: string | null;
  baseHeadcount: number | null;
  budgetAmount: number | null;
  baseAmount: number | null;
  status: IncentiveStatus;
  siteNote: string | null;
  hqNote: string | null;
  approvalDocumentId: string | null;
  submittedAt: string | null;
  confirmedAt: string | null;
  totalAmount: number;
  remainingAmount: number | null;
  siteEditable: boolean;
  hqEditable: boolean;
  /** 이 건을 조정할 수 있는가 — 서버가 건마다 판정한다(DOC-118 §18). 옛 서버는 안 보낸다. */
  canEdit?: boolean;
  awards: IncentiveAward[];
};

/** 저장할 때 보내는 한 줄. **이름도 금액도 없다** — 서버가 배치표에서 찾고 계산한다. */
export type AwardWrite = {
  personnelId: string;
  joinedOn: string | null;
  leftOn: string | null;
  months: number | null;
  contributionRate: number | null;
  /** 본사 경로에서만 쓰인다. 현장 경로로 보내면 서버가 무시하고 직전 조정액을 지킨다. */
  adjustedAmount?: number | null;
  note: string | null;
};

export type MyAward = {
  incentiveId: string;
  ownerName: string;
  grade: string | null;
  constructionStartDate: string | null;
  constructionEndDate: string | null;
  months: number | null;
  contributionRate: number | null;
  amount: number | null;
  adjustedAmount: number | null;
  payableAmount: number | null;
  note: string | null;
  confirmedAt: string | null;
};

/**
 * 잠긴 채 받는 것 — **금액이 하나도 없다**. 「물을 값이 있는가」뿐이다.
 *
 * 둘 다 0 이면 비밀번호를 묻지 않고 「없습니다」 라고 적는다. 없는 자료에 비밀번호를 묻는 화면은
 * 사람을 두 번 헛걸음시킨다(급여·고과의 `available` 과 같은 판단).
 */
export type MyIncentiveGate = { leadRoundCount: number; awardCount: number };

/** 재인증을 통과한 뒤에 오는 것. 금액은 여기부터다. */
export type MyIncentive = {
  /** 내가 장으로 서 있는 조직의 건. 대부분의 직원에게는 빈 배열이다. */
  leadRounds: IncentiveSummary[];
  /** 확정된 내 성과급. 확정 전 금액은 오지 않는다 — 협의 중인 숫자는 아직 내 돈이 아니다. */
  myAwards: MyAward[];
};

// ── 본사 ──────────────────────────────────────────────────────────────────────

export function useIncentives(status?: IncentiveStatus | '', ownerType?: IncentiveOwnerType) {
  const params = new URLSearchParams();
  if (status) params.set('status', status);
  if (ownerType) params.set('ownerType', ownerType);
  const query = params.toString();
  return useQuery({
    queryKey: ['hr-incentives', status ?? '', ownerType ?? ''],
    queryFn: () => apiFetch<IncentiveSummary[]>(`/v1/hr/incentives${query ? `?${query}` : ''}`),
    staleTime: 30_000,
  });
}

export type IncentiveAccess = { site: boolean; department: boolean };

/**
 * 성과급 건을 볼 수 있는 쪽 (DOC-118 §18) — 현장 건은 임원진·공사전략팀·현장대리인·SUPER_ADMIN.
 * 메뉴 「현장 성과급」 이 이 답으로 선다. 옛 서버(404)·실패면 답이 없고 메뉴도 서지 않는다.
 */
export function useIncentiveAccess(enabled = true) {
  return useQuery({
    queryKey: ['hr-incentives-access'],
    queryFn: () => apiFetch<IncentiveAccess>('/v1/hr/incentives/access'),
    enabled,
    retry: false,
    staleTime: 5 * 60_000,
  });
}

/**
 * 건 하나.
 *
 * `staleTime` 을 두지 않는다 — 이 화면은 **두 사람이 번갈아 손대는 판**이다(현장이 제출하고 본사가
 * 조정한다). 캐시가 가리는 것이 「아직 계산 중」 이 아니라 **남이 이미 바꾼 값**이라, 조정하려고
 * 연 화면이 제출 전 숫자를 보여 주면 그 위에 덮어쓰게 된다.
 */
export function useIncentive(id: string | null) {
  return useQuery({
    queryKey: ['hr-incentive', id],
    queryFn: () => apiFetch<IncentiveDetail>(`/v1/hr/incentives/${id}`),
    enabled: !!id,
    staleTime: 0,
    refetchOnMount: 'always',
  });
}

export type StandardWrite = {
  constructionStartDate: string | null;
  constructionEndDate: string | null;
  grade: string | null;
  baseHeadcount: number | null;
  budgetAmount: number | null;
  hqNote: string | null;
};

/**
 * 건 열기 — 현장이면 `siteId`, 부서면 `departmentId` 를 싣는다.
 *
 * 현장은 같은 현장에 두 건을 못 세운다(준공이 한 번이다). 부서는 끝나는 날이 없어 해마다 있을
 * 수 있으므로 막지 않는다 — 그 건들을 가르는 것은 부서가 아니라 평가 기간이다.
 */
export function useOpenIncentive() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: StandardWrite & {
      ownerType: IncentiveOwnerType;
      siteId?: string | null;
      departmentId?: string | null;
    }) => apiFetch<IncentiveDetail>('/v1/hr/incentives', { method: 'POST', body }),
    onSuccess: () => invalidate(queryClient),
  });
}

export function useSaveIncentiveStandards() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: StandardWrite & { id: string }) =>
      apiFetch<IncentiveDetail>(`/v1/hr/incentives/${id}`, { method: 'PUT', body }),
    onSuccess: () => invalidate(queryClient),
  });
}

/** 협의·조정 — 사람별 조정액. 제출된 건을 손대면 서버가 상태를 「협의·조정」으로 옮긴다. */
export function useSaveIncentiveAwards() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, awards, note }: { id: string; awards: AwardWrite[]; note?: string | null }) =>
      apiFetch<IncentiveDetail>(`/v1/hr/incentives/${id}/awards`, { method: 'PUT', body: { awards, note } }),
    onSuccess: () => invalidate(queryClient),
  });
}

/** 반려 — 평가자에게 되돌린다. **사유는 필수**다(서버가 막고, 화면도 비면 버튼을 안 연다). */
export function useReopenIncentive() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      apiFetch<IncentiveDetail>(`/v1/hr/incentives/${id}/reopen`, { method: 'POST', body: { reason } }),
    onSuccess: () => invalidate(queryClient),
  });
}

export function useRemoveIncentive() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiFetch<void>(`/v1/hr/incentives/${id}`, { method: 'DELETE' }),
    onSuccess: () => invalidate(queryClient),
  });
}

// ── 현장대리인·본인 ───────────────────────────────────────────────────────────

/** 잠금 화면이 읽는 것 — 건수뿐이다. 금액은 이 요청에 실리지 않는다. */
export function useMyIncentive() {
  const { data: session } = useSession();
  const userId = session?.user?.id ?? null;

  return useQuery({
    queryKey: ['mypage-incentive', userId],
    queryFn: () => apiFetch<MyIncentiveGate>('/v1/mypage/incentive'),
    enabled: !!userId,
    staleTime: 30_000,
  });
}

/**
 * 잠금 해제 — **쿼리가 아니라 뮤테이션이다**(급여·고과의 `useUnlockHrSensitive` 와 같은 이유).
 *
 * 캐시에 넣으면 화면을 떠난 뒤에도 금액이 메모리에 남는다. 받은 값은 패널이 들고 있다가
 * 탭을 벗어나면 언마운트와 함께 사라져 저절로 다시 잠긴다.
 */
export function useUnlockMyIncentive() {
  return useMutation({
    mutationFn: (password: string) =>
      apiFetch<MyIncentive>('/v1/mypage/incentive/unlock', { method: 'POST', body: { password } }),
  });
}

export function useMyIncentiveDetail(id: string | null) {
  return useQuery({
    queryKey: ['mypage-incentive-detail', id],
    queryFn: () => apiFetch<IncentiveDetail>(`/v1/mypage/incentive/${id}`),
    enabled: !!id,
    staleTime: 0,
    refetchOnMount: 'always',
  });
}

/** 임시저장. 한도를 넘어도 들어간다 — 막는 자리는 제출이다. */
export function useSaveMyIncentive() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, awards, note }: { id: string; awards: AwardWrite[]; note?: string | null }) =>
      apiFetch<IncentiveDetail>(`/v1/mypage/incentive/${id}/awards`, { method: 'PUT', body: { awards, note } }),
    onSuccess: () => invalidate(queryClient),
  });
}

/**
 * 본사 제출 — **저장과 한 번에** 간다(서버도 한 번에 한다).
 *
 * 나누면 고쳐 놓고 저장을 안 누른 채 제출한 건이 옛 숫자로 넘어간다. 한도를 넘으면 여기서
 * 400 이 돌아온다 — 초과분을 조용히 깎지 않는다.
 */
export function useSubmitMyIncentive() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, awards, note }: { id: string; awards: AwardWrite[]; note?: string | null }) =>
      apiFetch<IncentiveDetail>(`/v1/mypage/incentive/${id}/submit`, { method: 'POST', body: { awards, note } }),
    onSuccess: () => invalidate(queryClient),
  });
}

/**
 * 네 캐시를 함께 턴다.
 *
 * 같은 건을 **두 화면이 동시에 보고 있다** — 현장대리인이 제출하는 순간 본사 목록의 그 줄이
 * 바뀌어야 하고, 본사가 조정하면 현장 화면의 상태가 바뀌어야 한다. 한쪽만 털면 다른 쪽은
 * 새로고침할 때까지 지나간 상태를 자신 있게 보여 준다.
 */
function invalidate(queryClient: ReturnType<typeof useQueryClient>) {
  void queryClient.invalidateQueries({ queryKey: ['hr-incentives'] });
  void queryClient.invalidateQueries({ queryKey: ['hr-incentive'] });
  void queryClient.invalidateQueries({ queryKey: ['mypage-incentive'] });
  void queryClient.invalidateQueries({ queryKey: ['mypage-incentive-detail'] });
}

/**
 * 적는 동안 보이라고 하는 계산 — **서버와 같은 식, 같은 버림**이다
 * (`SiteIncentiveService.amountOf`). 다르면 저장 직후 숫자가 눈앞에서 바뀐다.
 *
 * 기준 금액이 없으면(한도나 기준 인원이 빔) 금액도 없다 — 0 이 아니다.
 */
export function previewAmount(baseAmount: number | null, rate: number | null): number | null {
  if (baseAmount === null || rate === null) return null;
  return Math.floor((baseAmount * rate) / 100);
}

/** 화면에 적는 금액. `null` 은 0 이 아니라 **비어 있음**이라 칸을 비운다. */
export function won(amount: number | null | undefined): string {
  return amount === null || amount === undefined ? '' : `${Math.trunc(amount).toLocaleString('ko-KR')}원`;
}
